import { useEffect, useReducer, useRef, useMemo } from 'react';
import FontIcon from '@material-ui/core/Icon';

import API from '../../../components/api';
import {
  SENSOR_STATUS,
  UNAVAILABLE_MESSAGE,
} from '../../../components/constants';
import { DATE_FORMATTER } from '../../../components/date-hour-formatter';

import ErrorMessage from '../../../elements/error-message';
import Title from '../../../elements/title';
import Text from '../../../elements/text';
import Button from '../../../elements/button';
import Table from '../../../elements/table';
import FieldWithTooltip from '../../../elements/field-with-tooltip';

import './browse.sass';

function StockUnallocatedBrowse() {
  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      loading: false,
      searchError: false,
      sensors: [],
      csvLoading: false,
    }
  );

  const isMountedRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getLastEvent = (lastEvent) => () => {
    if (!lastEvent) return null;
    return DATE_FORMATTER.PARTIAL(lastEvent);
  };

  const data = useMemo(() => {
    return state.sensors.map((row) => ({
      ...row,
      status: (
        <div className="sensor-status-section">
          <FieldWithTooltip
            className="sensor-status-section"
            status={SENSOR_STATUS[row.connectionState?.toUpperCase()]}
            getLastEvent={getLastEvent(row.lastEvent)}
          >
            <span
              className={`sensor-status ${row.connectionState?.toUpperCase()}`}
            />
          </FieldWithTooltip>
        </div>
      ),
      firstEvent: (
        <div>
          {row.firstEvent ? DATE_FORMATTER.PARTIAL(row.firstEvent) : '-'}
        </div>
      ),
      lastEvent: (
        <div>{row.lastEvent ? DATE_FORMATTER.PARTIAL(row.lastEvent) : '-'}</div>
      ),
      location:
        row.latitude && row.longitude ? (
          <div id="locationInfo" className="sensor-location">
            <a
              href={`https://www.google.com/maps?q=${row.latitude},${row.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontIcon className="material-icons">map</FontIcon>
            </a>
            <a
              href={`https://www.google.com/maps?q=${row.latitude},${row.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Text
                inline
                text={`${row.latitude.toFixed(4)}, ${row.longitude.toFixed(4)}`}
              />
            </a>
          </div>
        ) : (
          <Text inline text={UNAVAILABLE_MESSAGE} />
        ),
      sensorId: row.id,
    }));
  }, [state.sensors]);

  const csvData = useMemo(() => {
    return state.sensors.map((row) => ({
      SensorID: row.id,
      FirstEvent: row.firstEvent ? DATE_FORMATTER.PARTIAL(row.firstEvent) : '-',
      LastEvent: row.lastEvent ? DATE_FORMATTER.PARTIAL(row.lastEvent) : '-',
      Location:
        row.latitude && row.longitude
          ? `${row.latitude.toFixed(4)}, ${row.longitude.toFixed(4)}`
          : UNAVAILABLE_MESSAGE,
      Status: SENSOR_STATUS[row.connectionState?.toUpperCase()] || '-',
    }));
  }, [state.sensors]);

  const exportToCSV = () => {
    if (csvData.length === 0) return;

    setState({ csvLoading: true });

    setTimeout(() => {
      const headers = Object.keys(csvData[0]);
      const rows = csvData.map((row) =>
        headers.map((header) => `"${row[header] ?? ''}"`).join(',')
      );
      const csv = [headers.join(','), ...rows].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'unallocated_sensors.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setState({ csvLoading: false });
    }, 100);
  };

  const columns = useMemo(
    () => [
      { id: 'status', title: '', align: 'center' },
      { id: 'sensorId', title: 'Sensor ID', align: 'center' },
      { id: 'firstEvent', title: 'First Event', align: 'center' },
      { id: 'lastEvent', title: 'Last Event', align: 'center' },
      { id: 'location', title: 'Location', align: 'center' },
    ],
    []
  );

  const fetchUnallocatedDevices = () => {
    if (state.loading) return;

    console.log('Fetching unallocated devices...');
    setState({ loading: true, searchError: false });

    API.STOCK.UNALLOCATED_LIVE_SENSORS()
      .then(({ data }) => {
        console.log('Fetched unallocated devices:', data);
        if (!isMountedRef.current) return;
        setState({ loading: false, sensors: data || [] });
      })
      .catch((error) => {
        console.error(error);
        if (!isMountedRef.current) return;
        setState({ loading: false, searchError: true });
      });
  };

  return (
    <section id="page-stock-unallocated">
      <Title element="h4" text="Unallocated Devices" />
      <Text
        bold
        text="List of all unallocated devices. This request takes time to complete and show the results, do not leave the page."
      />

      {state.searchError && (
        <ErrorMessage message="Something went wrong while fetching devices. Please try again or contact support." />
      )}

      <div
        className="search-button"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <div>
          <Button
            disabled={state.loading}
            label="Search"
            loading={state.loading}
            type="button"
            click={fetchUnallocatedDevices}
          />
        </div>
        <div>
          <Button
            disabled={state.sensors.length === 0 || state.csvLoading}
            label="Export CSV"
            loading={state.csvLoading}
            type="button"
            click={exportToCSV}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={data}
        isLoading={state.loading}
        showPagination={false}
      />
    </section>
  );
}

export default StockUnallocatedBrowse;
